import React from 'react';
import { useCreateSupplierMutation } from '@/api/queries';
import type { CreateOutcome } from '@/utils';
import type { SupplierFormSubmit } from './components/supplierFormModal/useSupplierForm';

/**
 * Creating a supplier — the one definition, shared by the suppliers page and
 * the Control Center's "Add supplier".
 *
 * See `@/pages/brands/useCreateBrand` for why the create path lives in a hook
 * of its own rather than inside the page hook. The blank-as-null handling on
 * the three optional contact fields is already done by `useSupplierForm`, so
 * what arrives here is what goes on the wire.
 */
export const useCreateSupplier = () => {
    const createMutation = useCreateSupplierMutation();

    const create = React.useCallback(
        async (input: SupplierFormSubmit): Promise<CreateOutcome> => {
            const result = await createMutation.mutateAsync({
                name: input.name,
                serialNumber: input.serialNumber,
                phone: input.phone,
                email: input.email,
                imageUpload: input.imageUpload,
            });

            return result.success
                ? { ok: true }
                : {
                      ok: false,
                      message:
                          result.error.message ?? 'Failed to create supplier.',
                  };
        },
        [createMutation],
    );

    return { create, isCreating: createMutation.isPending };
};
